/**
 * Tab 2: Document Pipeline
 * Track content flow from SharePoint to the search index.
 */

import React, { useState } from 'react';
import { StatusBadge } from '../shared/StatusBadge';
import { LoadingPanel, ErrorPanel } from '../shared/LoadingPanel';
import {
  useWebhookSubscriptions,
  useIndexerInvocations,
  usePipelineStatus,
  formatRelativeTime,
} from '../hooks/useAdminData';
import { triggerDeltaSync } from '../../../api/adminApi';
import type { WebhookSubscription, IndexerInvocation } from '../../../types/admin';

function formatExpiry(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function friendlyLabel(sub: WebhookSubscription): { site: string; group: string } {
  if (sub.label.includes('doyleintranet'))
    return { site: 'DoyleIntranet Hub', group: 'Everyone' };
  if (sub.label.includes('remoteprogramming'))
    return { site: 'RemoteProgramming Spoke', group: 'RemoteProgrammers' };
  return { site: sub.label, group: sub.group_tag };
}

function invocationStatusBadge(status: IndexerInvocation['status']) {
  switch (status) {
    case 'indexed': return <StatusBadge status="healthy" label="INDEXED" />;
    case 'unchanged': return <StatusBadge status="healthy" label="UNCHANGED" />;
    case 'skipped': return <span className="adm-badge-info">SKIPPED</span>;
    case 'error': return <StatusBadge status="down" label="ERROR" />;
  }
}

export const DocumentPipeline: React.FC = () => {
  const webhooks = useWebhookSubscriptions(60_000);
  const invocations = useIndexerInvocations(60_000);
  const pipeline = usePipelineStatus(60_000);
  const [syncingLabel, setSyncingLabel] = useState<string | null>(null);
  const [showLegacy, setShowLegacy] = useState(false);

  const handleDeltaSync = async (label: string) => {
    setSyncingLabel(label);
    try {
      await triggerDeltaSync(label);
      await invocations.refresh();
    } catch {
      // error state handled by UI
    } finally {
      setSyncingLabel(null);
    }
  };

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-header">
        <h2 className="adm-tab-title">Document Pipeline</h2>
        <span className="adm-refresh-label">
          Updated {formatRelativeTime(webhooks.lastRefreshed)}
        </span>
      </div>

      {/* SharePoint Source Cards */}
      {webhooks.loading && !webhooks.data ? (
        <LoadingPanel message="Loading SharePoint sources..." />
      ) : webhooks.error && !webhooks.data ? (
        <ErrorPanel message={webhooks.error} onRetry={webhooks.refresh} />
      ) : webhooks.data ? (
        <div className="adm-source-grid">
          {webhooks.data.subscriptions.map((sub) => {
            const { site, group } = friendlyLabel(sub);
            const siteShort = sub.site_url.split('/sites/')[1] ?? sub.site_url;
            return (
              <div key={sub.subscription_id} className="adm-card adm-source-card">
                <div className="adm-card-title">
                  {site}{' '}
                  <span className="adm-group-tag">{group}</span>
                </div>
                <div className="adm-source-details">
                  <div className="adm-source-row">
                    <span className="adm-source-label">Site</span>
                    <span className="adm-mono">/sites/{siteShort}</span>
                  </div>
                  <div className="adm-source-row">
                    <span className="adm-source-label">Library</span>
                    <span>Knowledge Base/_Published</span>
                  </div>
                  <div className="adm-source-row">
                    <span className="adm-source-label">Delta Link</span>
                    <StatusBadge status="healthy" label="ACTIVE" />
                  </div>
                  <div className="adm-source-row">
                    <span className="adm-source-label">Subscription</span>
                    <span className="adm-mono" style={{ fontSize: '0.7rem' }}>
                      {sub.subscription_id.slice(0, 8)} — expires{' '}
                      {formatExpiry(sub.expiration)}
                    </span>
                  </div>
                </div>
                <button
                  className="adm-btn adm-btn-sm"
                  onClick={() => handleDeltaSync(sub.label)}
                  disabled={syncingLabel !== null}
                  style={{ marginTop: 12 }}
                >
                  {syncingLabel === sub.label ? 'Syncing...' : 'Re-run Delta Sync'}
                </button>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Recent Indexer Invocations */}
      <div className="adm-card" style={{ marginTop: 16 }}>
        <div className="adm-card-title">Recent Indexer Invocations</div>
        {invocations.loading && !invocations.data ? (
          <LoadingPanel message="Loading invocation history..." />
        ) : invocations.error && !invocations.data ? (
          <ErrorPanel message={invocations.error} onRetry={invocations.refresh} />
        ) : invocations.data && invocations.data.invocations.length > 0 ? (
          <table className="adm-table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Site</th>
                <th>Filename</th>
                <th>Status</th>
                <th>Chunks</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {invocations.data.invocations.slice(0, 20).map((inv, i) => (
                <tr key={i}>
                  <td className="adm-mono" style={{ fontSize: '0.72rem' }}>
                    {new Date(inv.timestamp).toLocaleString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </td>
                  <td>{inv.site}</td>
                  <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inv.filename}
                  </td>
                  <td>{invocationStatusBadge(inv.status)}</td>
                  <td className="adm-mono">{inv.chunks > 0 ? inv.chunks : '—'}</td>
                  <td className="adm-mono">
                    {inv.duration_ms > 0 ? `${(inv.duration_ms / 1000).toFixed(1)}s` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="adm-muted" style={{ marginTop: 8 }}>
            No recent invocations recorded.
          </p>
        )}
      </div>

      {/* Legacy Blob Source */}
      <div className="adm-card adm-legacy-card" style={{ marginTop: 16 }}>
        <button
          className="adm-legacy-toggle"
          onClick={() => setShowLegacy(!showLegacy)}
        >
          <span className="adm-warning-icon">&#9888;</span>
          <span style={{ fontWeight: 600 }}>Legacy Blob Source</span>
          <span className="adm-muted"> — Scheduled for retirement</span>
          <span style={{ marginLeft: 'auto', fontSize: '0.75rem' }}>
            {showLegacy ? '▲' : '▼'}
          </span>
        </button>
        {showLegacy && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #DEE2E6' }}>
            <div className="adm-source-row">
              <span className="adm-source-label">Container</span>
              <span className="adm-mono">devongptstorage/documents/</span>
            </div>
            <div className="adm-source-row">
              <span className="adm-source-label">Blob Trigger</span>
              <StatusBadge status="down" label="DISABLED" />
            </div>
            <div className="adm-source-row">
              <span className="adm-source-label">Remaining</span>
              <span>
                {pipeline.data?.search_indexer?.documents_indexed
                  ? `~${pipeline.data.search_indexer.documents_indexed.toLocaleString()} docs (blob-indexed chunks in index)`
                  : '~50-60 docs remaining to migrate'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
